/**
 * "Sources" row under a multi-part response.
 *
 * Previously this listed raw tool names, which tells a clinician WHICH
 * FUNCTION ran, not what kind of evidence it produced — `get_observations`
 * (a chart fact) and `get_simulation_result` (a model projection) looked
 * identical. This groups them by clinical source type so recorded data is
 * never mistaken for a model's output.
 *
 * Provenance is never colour-coded as severity: it says where a value came
 * from, not how dangerous it is. Emphasis is reserved for the two states a
 * clinician must not miss — model-derived and unsourced.
 */

export interface ToolProvenanceGroup {
  source: string;
  label: string;
  meaning: string;
  tools: string[];
}

export interface ToolProvenance {
  by_tool: Record<string, string>;
  groups: ToolProvenanceGroup[];
  has_generated: boolean;
  has_computed: boolean;
  has_unknown: boolean;
}

const prettify = (tool: string) => tool.replace(/_/g, ' ');

/** Source types a clinician must consciously notice rather than skim past. */
const NEEDS_EMPHASIS = new Set(['agent_generated', 'unknown']);

export function ToolSourceRow({
  tools,
  provenance,
}: {
  tools: string[];
  provenance?: ToolProvenance;
}) {
  // No provenance (older payload, or enrichment failed open) → the previous
  // flat list. Degrading to the old view is right; inventing a source is not.
  if (!provenance?.groups?.length) {
    return (
      <div className="flex flex-wrap gap-1.5 text-xs text-text-tertiary">
        <span className="font-medium">Sources:</span>
        {tools.map((t) => (
          <span key={t} className="rounded-md border border-border bg-surface-sunken px-2 py-0.5">
            {prettify(t)}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-tertiary">
      <span className="font-medium">Sources:</span>
      {provenance.groups.map((g) => (
        <span key={g.source} className="inline-flex items-center gap-1">
          <span
            title={g.meaning}
            className={
              NEEDS_EMPHASIS.has(g.source)
                ? 'rounded-md border border-foreground/25 bg-surface-sunken px-2 py-0.5 font-medium text-text-secondary'
                : 'rounded-md border border-border bg-surface-sunken px-2 py-0.5'
            }
          >
            {g.label}
          </span>
          <span className="text-text-tertiary/80">{g.tools.map(prettify).join(', ')}</span>
        </span>
      ))}
    </div>
  );
}
